/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useMemo } from "react";
import { safeText } from "../utils/theme1Helpers";

export function useDisplayData({
  apiProducts = [],
  apiCategories = [],
  apiCoupons = [],
  apiTags = []
}) {

  const displayProducts = useMemo(() => {
    const categoryMap = new Map();
    const subMap = new Map();

    const itemCategoryIdMap = new Map();
    const itemSubCategoryIdMap = new Map();

    apiCategories.forEach(cat => {
      categoryMap.set(Number(cat.categoryId), cat.categoryName);

      // Map items in this category
      if (Array.isArray(cat.items)) {
        cat.items.forEach(it => {
          const linkedId = Number(it.itemId);
          if (linkedId > 0) itemCategoryIdMap.set(linkedId, Number(cat.categoryId));
        });
      }

      (cat.subCategories || []).forEach(sub => {
        subMap.set(Number(sub.subCategoryId), {
          name: sub.subCategoryName,
          parentId: Number(cat.categoryId)
        });

        // Map items in this subcategory
        if (Array.isArray(sub.items)) {
          sub.items.forEach(it => {
            const linkedId = Number(it.itemId);
            if (linkedId <= 0) return;
            itemSubCategoryIdMap.set(linkedId, Number(sub.subCategoryId));
            if (!itemCategoryIdMap.has(linkedId)) {
              itemCategoryIdMap.set(linkedId, Number(cat.categoryId));
            }
          });
        }
      });
    });

    // Build tag map: itemId -> [tagNames]
    const itemTagsMap = new Map();
    if (Array.isArray(apiTags)) {
      apiTags.forEach(tag => {
        const tagName = tag.tagName || tag.name;
        if (tagName && Array.isArray(tag.linkedProductsCount)) {
          tag.linkedProductsCount.forEach(linked => {
            const itemId = Number(linked.itemId);
            if (!itemTagsMap.has(itemId)) {
              itemTagsMap.set(itemId, []);
            }
            itemTagsMap.get(itemId).push(tagName);
          });
        }
      });
    }


    return apiProducts.map(item => {
      const itemId = Number(item.id ?? item.itemId ?? 0);
      let categoryId = Number(item.categoryId || 0) || itemCategoryIdMap.get(itemId) || 0;
      let subCategoryId = Number(item.subCategoryId || 0) || itemSubCategoryIdMap.get(itemId) || 0;

      if (!categoryId && subCategoryId) {
        categoryId = Number(subMap.get(subCategoryId)?.parentId || 0);
      }

      if (!categoryId && item.categoryName) {
        const matched = apiCategories.find(
          (c) => String(c.categoryName || "").trim() === String(item.categoryName).trim()
        );
        if (matched) categoryId = Number(matched.categoryId || 0);
      }

      const rawVariants = Array.isArray(item.variants) ? item.variants : [];

      const normalizedVariants = rawVariants.map(v => ({
        ...v,
        __productId: itemId,
        __variantKey: `${itemId}_${v.variantId}`
      }));

      const inStock = normalizedVariants.filter(v => Number(v.stockQty) > 0);
      const firstVariant = inStock.length ? inStock[0] : null;

      // Combine tags from both sources
      const existingTags = Array.isArray(item.tags) ? item.tags.map(t => safeText(t?.name || t)) : [];
      const derivedTags = itemTagsMap.get(itemId) || [];
      const allTags = [...new Set([...existingTags, ...derivedTags])].filter(Boolean);
      const isBestseller = Boolean(item.isBestseller) || allTags.some(t => t.includes("اكثر مبيعا") || t.includes("الأكثر مبيعاً") || t.includes("Most Selling"));
      const isNewProduct = allTags.some(t => t.includes("جديد") || t.includes("وصل حديث") || t.includes("وصلنا حديث") || t.toLowerCase().includes("new"));
      const productDate = item.createdDate || item.createdAt || item.date || 0;

      return {
        id: itemId,
        name: item.name,
        description: item.description || "",

        mainImage: item.mainImage || item.mainImageUrl,
        mainImageUrl: item.mainImageUrl || item.mainImage,

        categoryId,
        category: categoryMap.get(categoryId) || item.categoryName || "غير مصنف",
        subCategoryId,
        subCategory: subMap.get(subCategoryId)?.name || null,
        tags: allTags,
        variants: normalizedVariants,
        selectedVariant: firstVariant ? { ...firstVariant } : null,
        isOutOfStock: !firstVariant,
        rating: Number(item.rating || 5),
        reviews: Number(item.reviews || 0),
        itemCode: item.itemCode,
        discountValue: item.discountValue,
        discountType: item.discountType,
        isBestseller,
        createdAt: productDate,
        isNewProduct: isNewProduct
      };
    });
  }, [apiProducts, apiCategories, apiTags]);

  const displayCategories = useMemo(() => {
    const countByCat = new Map();
    const countBySub = new Map();

    displayProducts.forEach(p => {
      countByCat.set(p.categoryId, (countByCat.get(p.categoryId) || 0) + 1);
      countBySub.set(p.subCategoryId, (countBySub.get(p.subCategoryId) || 0) + 1);
    });

    const cats = [{ id: 0, name: "الكل", icon: "🏪", subCategories: [], count: displayProducts.length }];

    apiCategories.forEach(cat => {
      cats.push({
        id: Number(cat.categoryId),
        name: cat.categoryName,
        icon: "📦",
        count: countByCat.get(Number(cat.categoryId)) || 0,
        subCategories: (cat.subCategories || []).map(sub => ({
          id: Number(sub.subCategoryId),
          name: sub.subCategoryName,
          count: countBySub.get(Number(sub.subCategoryId)) || 0
        }))
      });
    });

    return cats;
  }, [apiCategories, displayProducts]);

  const displayCoupons = useMemo(() => {
    const now = new Date();
    return apiCoupons.filter(c => {
      if (!c.couponName || !c.discount) return false;
      const from = new Date(c.fromDate);
      const to = new Date(c.toDate);
      return now >= from && now <= to;
    }).map(c => ({
      id: c.couponId,
      code: c.couponName.trim().toUpperCase(),
      discountValue: Number(c.discount),
      discountType: 2
    }));
  }, [apiCoupons]);

  const displayTags = useMemo(() => {
    return apiTags
      .filter(t => Number(t.productsCount || 0) > 0)
      .map(tag => ({
        id: tag.tagId,
        name: safeText(tag.tagName),
        itemIds: tag.linkedProductsCount?.map(p => p.itemId) || []
      }));
  }, [apiTags]);

  return { displayProducts, displayCategories, displayCoupons, displayTags };
}

export function filterProductsByDivision(products, selectedCategory, selectedSubCategory) {
  const catId = Number(selectedCategory || 0);
  const hasSub =
    selectedSubCategory !== null &&
    selectedSubCategory !== undefined &&
    selectedSubCategory !== "";
  const subId = hasSub ? Number(selectedSubCategory) : null;

  return (products || []).filter((p) => {
    if (catId && Number(p.categoryId) !== catId) return false;
    if (subId && Number.isFinite(subId) && Number(p.subCategoryId) !== subId) return false;
    return true;
  });
}